import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import { useSelector } from "react-redux";
import { asList, useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { thunkFetchLanguages } from "../../store/languages/actions";
import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { AppState } from "../../store";
import { Language } from "../../models/Translation";

interface LanguageMultiSelectProps extends Omit<SelectProps, "loading"> {
  label?: string;
  onSelected?: (value: ReadonlyArray<Language>) => void;
  variant?: "filled" | "outlined" | "standard";
  value?: ReadonlyArray<number>;
}

export const LanguageMultiSelect: FunctionComponent<
  LanguageMultiSelectProps
> = ({
  label = "Language",
  onSelected,
  onChange,
  variant = "standard",
  value = [],
  ...props
}) => {
  const languages = asList<Language>(
    useSelector<AppState, unknown>(s => s.languages)
  );
  const [state, setState] = useState<ReadonlyArray<Language>>([]);

  useLoadIfEmpty(!languages.length, thunkFetchLanguages);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let ids = event.target.value as Array<number>;
      let values = state.filter(x => ids.includes(x.id));

      onSelected(values ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (ids: ReadonlyArray<number>): ReactNode => {
    return state
      .filter(x => ids.includes(x.id))
      .map(x => x.name)
      .join(", ");
  };

  useEffect(() => {
    let filtered = languages.length > 0 && languages;

    if (filtered) {
      setState(
        [...filtered].sort((a: any, b: any) => a.name.localeCompare(b.name))
      );
    }
  }, [languages]);

  return (
    <FormControl
      style={props.style}
      variant={variant}
      className={props.className}
    >
      <InputLabel
        id="language-multi-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {label}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        labelId="language-multi-select-label"
        id="language-multi-select"
        autoWidth={true}
        input={<Input />}
        disabled={props.disabled}
        renderValue={value => renderNames(value as number[])}
      >
        {state &&
          state.length > 0 &&
          state.map(x => (
            <MenuItem key={x.id} value={x.id}>
              <Checkbox checked={value.indexOf(x.id) > -1} />
              <ListItemText primary={x.name} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
